  // Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
  //
  // This software is the property of WSO2 LLC. and its suppliers, if any.
  // Dissemination of any information or reproduction of any material contained
  // herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
  // You may not alter or remove any copyright or other notice from copies of this content.

  import { PieChart } from "@mui/x-charts/PieChart";
  import { Box, Typography, useTheme } from "@mui/material";
  import { generateThemeMatchingColors } from "../../utils/colors";

  interface Topic {
    topicId: number;
    name: string;
    wrongAnswerCount: number;
  }

  interface Category {
    categoryId: number;
    name: string;
    wrongAnswerCount: number;
    topics: Topic[];
  }

  interface AreasOfImprovement {
    categories: Category[];
    totalCount: number;
  }

  interface AreasOfImprovementProps {
    data: AreasOfImprovement;
  }

  export function AreasOfImprovement({ data }: AreasOfImprovementProps) {
    const theme = useTheme();

    if (data.categories.length === 0) {
      return null;
    }

    const chartData = data.categories?.map((category) => ({
      id: category.categoryId,
      value: category.wrongAnswerCount,
      label: category.name,
    }));

    let generatedColorsArray : string[] = generateThemeMatchingColors(theme, data.categories.length);
    
    const TopicsToolTip = (props?: any) => {
      const theme = useTheme();
      const { itemData, series } = props;
      const topics = data.categories?.find(
        (category) => category.name === series.data[itemData.dataIndex].label
      )?.topics;
      const color = series.data[itemData.dataIndex].color;
      return (
        <>
          <Box
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              border: `1px solid ${color}`,
              borderRadius: "4px",
              padding: "8px",
              boxShadow: theme.shadows[1],
              maxWidth: "200px",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: color,
                fontWeight: "bold",
                marginBottom: "4px",
              }}
            >
              {series.data[itemData.dataIndex].label} -{" "}
              {series.data[itemData.dataIndex].formattedValue}
            </Typography>
            {topics && topics.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                {topics?.map((topic) => (
                  <Typography
                    key={topic.topicId}
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {topic.name.replace(/_/g, " ")}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </>
      );
    };

    return (
      <div>
        <Box sx={{ mt: 3, mb: -1 }}>
          <Typography
            variant="inherit"
            gutterBottom
            color="primary"
            fontWeight="bold"
          >
            Areas for Improvement
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: -2 }}>
            <PieChart
              series={[
                {
                  data: chartData,
                  innerRadius: 30,
                  outerRadius: 100,
                  paddingAngle: 5,
                  cornerRadius: 5,
                  cx: 150,
                  cy: 150,
                  highlightScope: { faded: "global", highlighted: "item" },
                  faded: { innerRadius: 30, additionalRadius: -30, color: "gray" },
                  valueFormatter: (value) =>
                    `${((value.value / data.totalCount) * 100).toFixed(1)}%`,
                },
              ]}
              slotProps={{
                legend:{
                  hidden:true
                }
              }}
              tooltip={{
                trigger: "item",  
                itemContent: TopicsToolTip,
              }}
              width={300}
              height={300}
              colors={generatedColorsArray}
              margin={{ top: 0, right: 10, bottom: 10, left: 10 }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', ml: 0 }}>
              {chartData.map((item, index) => (
                <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      backgroundColor: generatedColorsArray[index],
                      mr: 1,
                    }}
                  />
                  <Typography textAlign={"left"} variant="caption">{item.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </div>
    );
  }
